import React, { useContext, useEffect, useState } from 'react';
import {
	IStyleSettingsCarouselUploaderContext,
	StyleSettingsCarouselUploaderContext,
} from '../styleSettingsCarouselUploaderState/styleSettingsCarouselUploaderContext';
import { postMediaFileForStyleSettings } from 'backend/additionalInformation';
import { openBrowseFile } from 'utils/openBrowseFile';
import { NewButton } from 'components/Button/NewButton/NewButton';
import { Icon, IconName } from 'components/Icon/Icon';
import Text from 'components/Text/Text';

interface IProps {
	videoMaxFileSizeMB: number;
	onUpload: ({ sourceUrl, fileId }: { sourceUrl: string; fileId: string }) => void;
	showGroupLoader: () => void;
	hideGroupLoader: () => void;
}

export const UploadVideoNew: React.FC<IProps> = ({
	videoMaxFileSizeMB,
	onUpload,
	showGroupLoader,
	hideGroupLoader,
}) => {
	const { changeTypeOfErrorModalWindow } = useContext<IStyleSettingsCarouselUploaderContext>(
		StyleSettingsCarouselUploaderContext,
	);
	const [dataToUpload, setDataToUpload] = useState<{
		status: 'success' | 'error';
		sourceUrl?: Nullable<string>;
		fileId?: Nullable<string>;
	}>();

	useEffect(() => {
		if (dataToUpload?.status === 'success' && dataToUpload.sourceUrl && dataToUpload.fileId) {
			onUpload({ sourceUrl: dataToUpload.sourceUrl, fileId: dataToUpload.fileId });
			hideGroupLoader();
		} else if (dataToUpload?.status === 'error') {
			hideGroupLoader();
			changeTypeOfErrorModalWindow('fileUploadError');
		}
		setDataToUpload(undefined);
	}, [dataToUpload]);

	const sendForm = async (video: File) => {
		const formData = new FormData();
		formData.append('file', video, video.name);
		const sourceUrl = URL.createObjectURL(video);
		try {
			showGroupLoader();
			const response = await postMediaFileForStyleSettings(formData);
			setDataToUpload({ status: 'success', sourceUrl, fileId: response.data.fileId });
		} catch {
			setDataToUpload({ status: 'error' });
		}
	};

	const handleChooseFile = (files: Nullable<FileList>) => {
		const videoFile = files?.[0];

		if (!videoFile) {
			return;
		}

		if (videoFile.size > 1000 * 1000 * videoMaxFileSizeMB) {
			changeTypeOfErrorModalWindow('videoFileSizeExceeded');
		} else if (videoFile.type === 'video/mp4' || videoFile.type === 'video/x-m4v') {
			sendForm(videoFile);
		} else {
			changeTypeOfErrorModalWindow('invalidFileFormat');
		}
	};

	const handleClick = () => {
		openBrowseFile({ onChoose: handleChooseFile, accept: 'video/mp4,video/x-m4v' });
	};

	return (
		<NewButton
			size="small"
			iconLeft={<Icon name={IconName.upload} size="lg" />}
			testId="uploadVideoButton"
			onClick={handleClick}>
			<Text translationNameSpace="form" translationKey="uploadVideoButton" asFragment={true} />
		</NewButton>
	);
};
